<template>
  <div v-if="fieldType === campaignFieldTypes.PHONE_NUMBER" id="call_via" class="form_col">
    <span>Call Via</span>
    <a-select
      :value="modelValue"
      @update:value="handleUpdate"
      class="w-full"
      placeholder="Select a phone number"
      :options="phoneNumbers"
      :field-names="{ label: 'phone_number', value: 'id' }"
    >
      <template #suffixIcon>
        <ThemeImage
          :icon="imageNames.BOTTOM_ARROW"
          alt="bottom_arrow"
          class="size-4"
        />
      </template>
    </a-select>
  </div>

  <div v-else-if="fieldType === campaignFieldTypes.VOICE_AGENT" id="voice_agent" class="form_col">
    <span>Voice Agent</span>
    <a-select
      :value="modelValue"
      @update:value="handleUpdate"
      @search="handleSearch"
      class="w-full"
      placeholder="Select a voice agent"
      :options="voiceAgents"
      :field-names="{ label: 'name', value: 'id' }"
      show-search
      :filter-option="false"
    >
      <template #suffixIcon>
        <ThemeImage
          :icon="imageNames.BOTTOM_ARROW"
          alt="bottom_arrow"
          class="size-4"
        />
      </template>
    </a-select>
  </div>

  <div v-else-if="fieldType === campaignFieldTypes.COOLDOWN" id="cooldown_duration" class="form_col">
    <span>Cooldown duration between two calls</span>
    <CustomInput
      format="number"
      :modelValue="modelValue"
      @update:modelValue="handleUpdate"
      placeholder="Enter duration"
      type="primary"
      add-after-on="seconds"
      :min="0"
      :max="180"
    />
  </div>

  <div v-else-if="fieldType === campaignFieldTypes.RECORDING" id="call_recording">
    <a-checkbox
      :checked="modelValue"
      @update:checked="handleUpdate"
      class="text-[13px]"
    >
      Enable default call recording for all calls in this campaign
    </a-checkbox>
  </div>

  <div v-else-if="fieldType === campaignFieldTypes.PARALLEL_CALLS" id="parallel_calls" class="form_col">
    <span>Number of parallel calls</span>
    <a-select
      :value="modelValue"
      @update:value="handleUpdate"
      class="w-full"
      placeholder="Select number of parallel calls"
    >
      <template #suffixIcon>
        <ThemeImage
          :icon="imageNames.BOTTOM_ARROW"
          alt="bottom_arrow"
          class="size-4"
        />
      </template>
      <a-select-option v-for="index in 2" :key="index" :value="index">
        {{ index }}
      </a-select-option>
    </a-select>
  </div>

  <AudienceTypeSelector
    v-else-if="fieldType === campaignFieldTypes.AUDIENCE"
    :source="source"
    :formData="formData"
  />

  <CustomContainer
    v-else-if="fieldType === campaignFieldTypes.MAP_VARIABLES"
    class="cursor-pointer"
    @click="handleOpenModal"
  >
    <div class="flex flex-row gap-2 items-center w-fit shrink-0">
      <ThemeImage
        :icon="imageNames.CURLY_BRACE_GREY"
        alt="curly_brace_icon"
        class="size-4"
      />
      <span class="text-color">Map Variables</span>
    </div>
  </CustomContainer>

  <div v-else-if="fieldType === campaignFieldTypes.DNC_FOLLOW" id="dnc_follow" class="flex flex-row items-center">
    <a-checkbox
      :checked="modelValue"
      @update:checked="handleUpdate"
      class="text-[13px]"
    >
      Respect Do-Not-Call (DNC) lists
    </a-checkbox>
    
    <a-tooltip placement="top">
      <template #title>
        Skip numbers found in your DNC lists
      </template>
      <ThemeImage
        class="size-4"
        :icon="imageNames.INFO_GREY"
        alt="info_icon"
      />
    </a-tooltip>
  </div>

  <div v-else-if="fieldType === campaignFieldTypes.TIMEZONE_SETTINGS">
    <div id="local_time_zone">
      <a-checkbox
        :checked="timezoneData.useLocalTimeZone"
        @update:checked="handleTimezoneToggle"
        class="text-[13px]"
      >
        Use the lead/contact's local time zone to call during their active hours
      </a-checkbox>
    </div>

    <div v-if="timezoneData.useLocalTimeZone" class="flex flex-col gap-4 mt-4">
      <div class="form_col">
        <span>Active Days</span>
        <a-select
          :value="timezoneData.dayAllowed"
          @update:value="handleActiveDaysUpdate"
          class="w-full mt-2"
          mode="multiple"
          placeholder="Select active days"
          :options="activeDaysOptions"
        >
          <template #suffixIcon>
            <ThemeImage
              :icon="imageNames.BOTTOM_ARROW"
              alt="bottom_arrow"
              class="size-4"
            />
          </template>
        </a-select>
      </div>

      <div class="flex flex-col gap-2">
        <div class="flex justify-between gap-2">
          <div class="w-6/12 form_col">
            <span class="text-[13px] secondary_color proxima_nova font-normal">
              Start Time
            </span>
            <a-select 
              :value="timezoneData.startTime"
              @update:value="handleStartTimeUpdate"
              showSearch 
              :options="filteredStartTimeIntervals"
              :status="timeError ? 'error' : ''"
              :class="{ 'error-select': timeError }"
            >
              <template #suffixIcon>
                <ThemeImage
                  :icon="imageNames.BOTTOM_ARROW"
                  alt="bottom_arrow"
                  class="size-4"
                />
              </template>
            </a-select>
          </div>
          <div class="w-6/12 form_col">
            <span class="text-[13px] secondary_color proxima_nova font-normal">
              End Time
            </span>
            <a-select 
              :value="timezoneData.endTime"
              @update:value="handleEndTimeUpdate"
              showSearch 
              :options="filteredEndTimeIntervals"
              :status="timeError ? 'error' : ''"
              :class="{ 'error-select': timeError }"
            >
              <template #suffixIcon>
                <ThemeImage
                  :icon="imageNames.BOTTOM_ARROW"
                  alt="bottom_arrow"
                  class="size-4"
                />
              </template>
            </a-select>
          </div>
        </div>
        <span v-if="timeError" class="error_message_color text-xs">
          {{ timeError }}
        </span>
      </div>
    </div>
  </div>

  <div v-else-if="fieldType === campaignFieldTypes.ACTION_BUTTONS" class="flex flex-row gap-2 justify-end">
    <a-button class="secondary_medium" @click="handleCancel">
      Cancel
    </a-button>

    <a-button
      class="primary_medium"
      @click="handleCreate"
      :loading="loading"
      :disabled="disabled"
    >
      Create Campaign
    </a-button>
  </div>
</template>

<script setup>
import { computed, watch, ref, nextTick } from "vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { campaignFieldTypes } from "@/src/constants/callCampaignConstants.js";
import { activeDaysOptions } from "@/src/constants/sequenceConstants.js";
import { generateTimeOptions, validateTimeRange, filterTimeOptions } from "@/src/utils/utils.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import CustomInput from "@/src/components/CustomInput.vue";
import AudienceTypeSelector from "@/src/components/telephony_components/campaigns/AudienceTypeSelector.vue";
import CustomContainer from "@/src/components/CustomContainer.vue";

const props = defineProps({
  fieldType: {
    type: String,
    required: true,
  },
  modelValue: {
    type: [String, Number, Boolean],
    default: null,
  },
  phoneNumbers: {
    type: Array,
    default: () => [],
  },
  voiceAgents: {
    type: Array,
    default: () => [],
  },
  source: {
    type: String,
    default: "",
  },
  formData: {
    type: Object,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  onSearch: {
    type: Function,
    default: null,
  },
  timezoneData: {
    type: Object,
    default: () => ({
      useLocalTimeZone: false,
      dayAllowed: [],
      startTime: "9:00am",
      endTime: "5:00pm",
    }),
  },
  scrollContainerRef: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(["update:modelValue", "cancel", "create", "update:timezoneData", "update:timeError", "openModal"]);
const timeError = ref("");

const handleUpdate = (value) => {
  try {
    emit("update:modelValue", value);
  } catch (error) {
    console.error("Error updating field value:", error);
  }
};

const handleCancel = () => {
  try {
    emit("cancel");
  } catch (error) {
    console.error("Error handling cancel:", error);
  }
};

const handleCreate = () => {
  try {
    emit("create");
  } catch (error) {
    console.error("Error handling create:", error);
  }
};

const handleOpenModal = () => {
  try {
    emit("openModal");
  } catch (error) {
    console.error("Error opening modal:", error);
  }
};

const handleSearch = (searchValue) => {
  try {
    if (props.onSearch && typeof props.onSearch === 'function') {
      props.onSearch(searchValue);
    }
  } catch (error) {
    console.error("Error handling search:", error);
  }
};

const allTimeIntervals = generateTimeOptions();

const filteredStartTimeIntervals = computed(() => {
  const filtered = filterTimeOptions(allTimeIntervals, props.timezoneData.endTime, 'before');
  return filtered.map(time => ({
    value: time,
    label: time
  }));
});

const filteredEndTimeIntervals = computed(() => {
  const filtered = filterTimeOptions(allTimeIntervals, props.timezoneData.startTime, 'after');
  return filtered.map(time => ({
    value: time,
    label: time
  }));
});

const timeIntervals = computed(() => {
  return allTimeIntervals.map(time => ({
    value: time,
    label: time
  }));
});

const handleTimezoneToggle = (value) => {
  try {
    emit("update:timezoneData", {
      ...props.timezoneData,
      useLocalTimeZone: value,
    });
  } catch (error) {
    console.error("Error handling timezone toggle:", error);
  }
};

const handleActiveDaysUpdate = (value) => {
  try {
    emit("update:timezoneData", {
      ...props.timezoneData,
      dayAllowed: value,
    });
  } catch (error) {
    console.error("Error handling active days update:", error);
  }
};

const handleStartTimeUpdate = (value) => {
  try {
    emit("update:timezoneData", {
      ...props.timezoneData,
      startTime: value,
    });
  } catch (error) {
    console.error("Error handling start time update:", error);
  }
};

const handleEndTimeUpdate = (value) => {
  try {
    emit("update:timezoneData", {
      ...props.timezoneData,
      endTime: value,
    });
  } catch (error) {
    console.error("Error handling end time update:", error);
  }
};

const handleTimezoneScroll = async (useLocalTimeZone) => {
  try {
    if (!props.scrollContainerRef || props.fieldType !== campaignFieldTypes.TIMEZONE_SETTINGS) return;
    
    await nextTick();
    
    if (useLocalTimeZone) {
      props.scrollContainerRef.scrollTo({ 
        top: props.scrollContainerRef.scrollHeight, 
        behavior: 'smooth' 
      });
    }
  } catch (error) {
    console.error("Error handling timezone scroll:", error);
  }
};

watch(
  () => [props.timezoneData.startTime, props.timezoneData.endTime],
  ([startTime, endTime]) => {
    if (startTime && endTime) {
      timeError.value = validateTimeRange(startTime, endTime) || "";
      emit("update:timeError", timeError.value);
    }
  },
  { immediate: true }
);

watch(
  () => props.timezoneData.useLocalTimeZone,
  handleTimezoneScroll
);
</script>

<style scoped>
:deep(.error-select .ant-select-selector) {
  border: 1px solid var(--error-color) !important;
}
</style>

